import React, { ComponentType, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import useLayoutEffect from '../../../hooks/useLayoutEffect';

import './glider.scss';
import './glider-wrapper.scss';

// GLIDER VARIANT should EXTEND the ones of COMPONENTS to be EMBEDDED
// export const GLIDER_VARIANT_RESULTS = 'results';
// export const GLIDER_VARIANT_REPORTS = 'reports';
export type GliderVariant = string;

export type BorderedType = 'top-bottom' | 'bottom';

// This is the interface all COMPONENTS to be EMBEDDED must EXTEND, see organisms/match-change-table/match-change-table.tsx:19
export interface IGliderComponent<V = GliderVariant> {
  activeSlideIndex?: number;
  trackingData?: object;
  variant?: V;
  onItemSelectedCallback?: (id: number) => void;
}
export interface IEmbeddedComponent extends IGliderComponent {}
export interface IGliderWrapper<IEmbeddedComponent> {
  // REACT-GLIDER props
  draggable?: boolean;
  exactWidth?: boolean;
  hasArrows?: boolean;
  hasDots?: boolean;
  itemWidth?: number;
  scrollLock?: boolean;
  slidesToScroll?: number;
  slidesToShow?: number;
  duration?: number;
  // Old DS GLIDER props (to be checked if needed)
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  bordered?: BorderedType;
  topOffset?: boolean;
  // gliderVariant?: GliderVariantType;
  // className?: string;
  // forceRenderSkeleton?: boolean;
  // scrollPointBreakpoint?: number;
  // scrollToPage?: number;
  // scrollToSlide?: number;
  // EMBEDDED COMPONENT to render (passed as PROP)
  children?: ReactNode;
  Component?: ComponentType<IEmbeddedComponent>;
  propsComponent?: IEmbeddedComponent;
}

const getClasses = (params: { bordered?: string; topOffset?: boolean; variant?: string }) => {
  const { bordered, topOffset, variant } = params;

  const classes = ['glider-wrapper'];

  if (variant) {
    classes.push(` glider-wrapper--${variant}`);
  }

  if (bordered) {
    classes.push(`glider-wrapper--bordered-${bordered}`);
  }

  if (topOffset) {
    classes.push(`glider-wrapper--top-offset`);
  }

  return classes.join(' ');
};

const GliderWrapper = <IEmbeddedComponent extends IGliderComponent>({
  /* istanbul ignore next: default value */ draggable = false,
  /* istanbul ignore next: default value */ exactWidth = false,
  /* istanbul ignore next: default value */ scrollLock = false,
  /* istanbul ignore next: default value */ slidesToScroll = 1,
  /* istanbul ignore next: default value */ duration = 0.5,
  hasArrows,
  iconLeft,
  iconRight,
  children,
  itemWidth,
  bordered,
  topOffset,
  Component,
  propsComponent,
}: IGliderWrapper<IEmbeddedComponent>): ReactElement<IEmbeddedComponent> => {
  // DYNAMIC POSITIONING: if glider-track.width < viewport.width then justify-content=center (using component hook)
  // glider-track.width = itemWidth * propsComponent.matchList.length
  const [GliderComponentLazy, setGliderComponentLazy] = useState<any>(null);
  const gliderWrapperRef = useRef<HTMLDivElement>(null);
  const gliderTrackRef = useRef<HTMLDivElement>(null); // GliderTrack DOM node
  const gliderRef = useRef<any>(null);
  const activeSlideIndex = Component && propsComponent && propsComponent.activeSlideIndex;
  const variant = Component && propsComponent && propsComponent.variant;

  useEffect(() => {
    if (GliderComponentLazy) {
      return;
    }
    if (gliderWrapperRef.current) {
      import('react-glider')
        .then((GliderModule) => {
          console.log('import react-glider GliderWrapper', GliderModule);
          console.log('import react-glider GliderWrapper', {
            gliderTrackRef,
            gliderRef,
            gliderWrapperRef,
            GliderComponentLazy,
          });

          setGliderComponentLazy(GliderModule.default);
        })

        // eslint-disable-next-line no-console
        .catch((err) => console.log('Unable to load react-glider', err));
    }
  }, [gliderWrapperRef, GliderComponentLazy]);

  useEffect(() => {
    if (gliderRef?.current && activeSlideIndex !== undefined) {
      gliderRef.current.scrollItem(activeSlideIndex - 1);
    }
  }, [activeSlideIndex, GliderComponentLazy]);

  // To avoid FLICKERING of ARROWS show them only AFTER 1st rendering
  useLayoutEffect(() => {
    if (hasArrows && gliderWrapperRef.current) {
      const arrowPrev: HTMLButtonElement | null = gliderWrapperRef.current.querySelector('button.glider-prev');
      const arrowNext: HTMLButtonElement | null = gliderWrapperRef.current.querySelector('button.glider-next');
      // console.log('GliderWrapper...', { hasArrows, arrowPrev, arrowNext });
      if (arrowPrev && arrowNext) {
        // Give REACT-GLIDER the time to init its code...
        setTimeout(() => {
          // ...then RESTORE arrows CSS
          arrowPrev.style.transition = 'all .4s ease-in-out';
          arrowNext.style.transition = 'all .4s ease-in-out';
          arrowPrev.style.display = 'block';
          arrowNext.style.display = 'block';
          // console.log('...done!', { arrowPrev, arrowNext });
        }, 1000);
      }
    }
  }, [hasArrows, gliderWrapperRef, GliderComponentLazy]);

  // CSS CLASS NAMES: can't use makeClassNames() due to GLIDER be inside CSS_PREFIXES_WHITELIST in both /gulpfile.js and /.storybook/postcss.config.js
  // After MIGRATION to this NEW GLIDER, will import 'glider-js/glider.min.css' and REMOVE glider from CSS_PREFIXES_WHITELIST
  const classNameWrapper = getClasses({ bordered, topOffset, variant });

  const uniqueId = crypto.randomUUID() ?? Date.now();

  return (
    <div ref={gliderWrapperRef} className={classNameWrapper}>
      {gliderWrapperRef.current && GliderComponentLazy && (
        <GliderComponentLazy
          ref={gliderRef}
          className="glider-component"
          // REACT-GLIDER PROPS
          draggable={draggable}
          duration={duration || 0} // according to workaround
          exactWidth={exactWidth}
          hasArrows={hasArrows}
          iconLeft={iconLeft}
          iconRight={iconRight}
          itemWidth={itemWidth}
          resizeLock={false} // set to false to enable animate on scroll
          scrollLock={scrollLock}
          // scrollLockDelay={0} // To be checked
          scrollToSlide={activeSlideIndex}
          slidesToScroll={slidesToScroll}
          slidesToShow="auto"
          skipTrack // Needs additional <div className="glider-track--custom"> around items, see https://github.com/kevinfarrugia/react-glider/issues/120#issuecomment-1266770002
          // slidesToShow={slidesToShow}
          // DS PROPS
          // scrollPointBreakpoint={scrollPoint}
          // topOffset={topOffset}
          id={uniqueId}
        >
          {/* Each GLIDER should be UNIQUE for MUTATION OBSERVER to work if SEVERAL GLIDERS are on the SAME PAGE
        <div
          ref={refGliderTrack}
          className={`glider-track--custom glider-track--${self.crypto.randomUUID() ?? Date.now()}`}
        > */}
          <div ref={gliderTrackRef} className="glider-track--custom glider-track">
            {Component && propsComponent ? (
              <Component {...propsComponent} activeSlideIndex={activeSlideIndex} variant={variant} />
            ) : (
              children
            )}
          </div>
        </GliderComponentLazy>
      )}
    </div>
  );
};

export default GliderWrapper;
